"use client";

import { useState, type FormEvent } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import type { AuthEntryPoint } from "@/lib/analytics/types";
import { track } from "@/lib/analytics/tracker";
import type { SignedIn } from "@/lib/auth/sign-in/result";
import { signUpWithPassword } from "../sign-in-actions";
import { SocialSignIn } from "../social-sign-in";
import { AuthField } from "./auth-field";
import { AuthError } from "./auth-messages";
import { useAuthCompletion } from "./use-auth-completion";

export interface SignUpStepProps {
  entryPoint: AuthEntryPoint;
  initialEmail?: string;
  quoteClaimToken?: string;
  showProviders: boolean;
  onSignedIn: (result: SignedIn) => void | Promise<void>;
  onNeedsCode: () => void;
  onLogIn: (email: string) => void;
}

function useSignUpFields(initialEmail: string) {
  const [fields, setFields] = useState({ firstName: "", lastName: "", email: initialEmail, password: "" });
  const set = (key: keyof typeof fields) => (value: string) => setFields((current) => ({ ...current, [key]: value }));
  return { fields, set };
}

export function SignUpStep(props: SignUpStepProps) {
  const { fields, set } = useSignUpFields(props.initialEmail ?? "");
  const completion = useAuthCompletion({
    entryPoint: props.entryPoint,
    surface: "password",
    method: "sign_up",
    onSignedIn: props.onSignedIn,
    onNeedsCode: props.onNeedsCode,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    track("auth.started", { entry_point: props.entryPoint, method: "password" });
    void completion.run(() => signUpWithPassword({ ...fields, quoteClaimToken: props.quoteClaimToken }));
  };

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="grid grid-cols-2 gap-3">
        <AuthField label="First name" autoComplete="given-name" autoFocus value={fields.firstName} onChange={set("firstName")} />
        <AuthField label="Last name" autoComplete="family-name" value={fields.lastName} onChange={set("lastName")} />
      </div>
      <AuthField label="Email" type="email" autoComplete="email" value={fields.email} onChange={set("email")} />
      <AuthField
        label="Password"
        type="password"
        autoComplete="new-password"
        hint="At least 10 characters."
        value={fields.password}
        onChange={set("password")}
      />
      <AuthError message={completion.error} />
      <Button type="submit" variant="primary" className="w-full" disabled={completion.isBusy}>
        {completion.isBusy ? "Creating your account…" : "Create account"}
      </Button>
      {props.showProviders ? <SocialSignIn /> : null}
      <p className="text-center text-xs text-muted-foreground">
        By creating an account you agree to the{" "}
        <Link href="/terms-conditions" target="_blank" rel="noopener noreferrer" className="underline">Terms</Link> and{" "}
        <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</Link>.
      </p>
      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <button type="button" className="font-medium text-foreground underline disabled:opacity-50" disabled={completion.isBusy} onClick={() => props.onLogIn(fields.email)}>
          Log in
        </button>
      </p>
    </form>
  );
}
